/* istanbul ignore file */

import { createTheme, alpha, Theme } from '@mui/material/styles';

import { createTggPalette } from './colours/palette';
import { lightPrimitives } from './colours/primitives';
import { ThemePalette } from './palette.type';
import {
    getRebrandingColorCreator,
    isRebrandingCreator,
    pxToRem,
    rem,
    spacingCreator,
} from './theme.utils';

export const palette: ThemePalette = {
    primary: {
        main: '#2e94ff',
        dark: '#2a85f0',
        light: '#F1F9FD', // no specified lighter blue
    },
    secondary: {
        main: '#b2ff00',
        dark: '#7AA130',
        light: '#f7ffe5',
    },
    common: {
        white: '#FFFFFF',
        blue: '#0e0066',
        red: '#db202c',
        lightGrey: '#efefef',
    },
    grey: {
        // tints
        900: '#cce3fa',
        200: '#d7e8fa',
        100: '#e5effa',
        50: '#eef3f9',
    },
    success: {
        main: '#08be07',
        dark: '#587423', // not sure what goes here
        light: '#f2fcf2',
    },
    warning: {
        main: '#F58A1C',
        light: '#fefaf4',
    },
    error: {
        main: '#e22918',
        dark: '#fef5f5', // this is a lighter red
    },
    pino: {
        ultimate: '#f04f29',
        ultimateAlternate: '#fff6f4',
        standard: '#2e94ff',
        standardAlternate: '#eef3f9',
        offPeak: '#efefef',
        offPeakAlternate: '#fafafa',
    },
    mbtOptimizationPino: {
        ultimate: '#f04f29',
        ultimateBackground: '#feeeea',
        standard: '#2e94ff',
        offPeak: '#737373',
        offPeakBackground: '#f1f1f1',
    },
    other: {
        yellowPending: '#ea9628',
        //non-palette colors are listed here
        black: '#000000',
        darkBlue: '#34287d',
        neutralGrey: '#8d8c8c',
        paleBlue: '#bacfe3',
        buttonHover: '#00000033',
        redHover: '#e229184d',
        neutralBlue: '#A3B6C8',
        actionBlue: '#2576cc',
        shadowGrey: '#00000017',
        transparentGrey: '#eef3f900',
        transparentBlue: '#2e94ff33',
        promoHover: '#bacfe3',
    },
    product: {
        silverGrey: '#efefef',
    },
    energyBlue: {
        500: '#0050ef',
    },
    navBar: {
        hover: '#0049d9',
    },
};

export const boxShadow = `0 0 0.8rem 0 ${alpha(palette.primary.main, 0.4)}`;
export const lightGreyBoxShadow = `0 0 0 2px rgba(0, 0, 0, 0.1);`;

export const containerSmallWidth = 62.8; // '62.8rem';
export const containerLargeWidth = 84.5; //'84.5rem';

declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        largeDesktop: true; // adds the `largeDesktop` breakpoint
        extraLargeDesktop: true; // adds the `extraLargeDesktop` breakpoint
    }
}

export const createTggTheme = (isRebrandingEnabled?: boolean): Theme => {
    const tggSpacing = spacingCreator(isRebrandingEnabled);
    const theme = createTheme({
        palette: { ...palette, tgg: createTggPalette(lightPrimitives) },
        breakpoints: {
            values: {
                mobile: 440,
                desktop: 768,
                largeDesktop: 1440,
                extraLargeDesktop: 1920,
            },
        },
        typography: {
            fontFamily: 'thegymgroupsans-Bold',
            htmlFontSize: 10,
            button: {
                textTransform: 'none',
            },
        },
        shape: {
            borderRadius: 3,
        },
        spacing: pxToRem,
    });

    const themeWithOverrides = createTheme({
        ...theme,
        components: {
            MuiTypography: {
                styleOverrides: {
                    root: {
                        '&.MuiTypography-h1&.MuiTypography-gutterBottom': {
                            marginBottom: '1rem',
                            [theme.breakpoints.up('desktop')]: {
                                marginBottom: '2rem',
                            },
                        },
                        '&.MuiTypography-h2&.MuiTypography-gutterBottom': {
                            marginBottom: '1rem',
                            [theme.breakpoints.up('desktop')]: {
                                marginBottom: '1.5rem',
                            },
                        },
                        '&.MuiTypography-h3&.MuiTypography-gutterBottom': {
                            marginBottom: '1rem',
                            [theme.breakpoints.up('desktop')]: {
                                marginBottom: '1.5rem',
                            },
                        },
                        '&.MuiTypography-h4&.MuiTypography-gutterBottom': {
                            marginBottom: '1rem',
                        },
                        '&.MuiTypography-caption&.MuiTypography-gutterBottom': {
                            marginBottom: '2rem',
                        },
                        h5: {
                            '&.MuiTypography-gutterBottom': {
                                marginBottom: '1rem',
                            },
                        },
                        h6: {
                            '&.MuiTypography-gutterBottom': {
                                marginBottom: '1rem',
                            },
                        },
                        '&.MuiTypography-subtitle1&.MuiTypography-gutterBottom':
                            { marginBottom: '1.5rem' },
                        '&.MuiTypography-body1&.MuiTypography-gutterBottom': {
                            marginBottom: '2rem',
                        },
                    },
                },
            },
        },
    });
    const overrideTypography = {
        h1: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.8), 7),
            lineHeight: tggSpacing(rem(3), 7.5),
            letterSpacing: tggSpacing(rem(-0.05), -0.125),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.6), 9),
                lineHeight: tggSpacing(rem(3.6), 9),
            },
        },
        h2: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.1), 5.25),
            lineHeight: tggSpacing(rem(2.7), 6.75),
            letterSpacing: tggSpacing(rem(-0.05), -0.125),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.7), 6.75),
                lineHeight: tggSpacing(rem(3.2), 8),
            },
        },
        h3: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.3), 5.75),
            lineHeight: tggSpacing(rem(2.6), 6.5),
            letterSpacing: tggSpacing(rem(-0.04), -0.1),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.6), 6.5),
                lineHeight: tggSpacing(rem(3), 7.5),
            },
        },
        h4: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(2.1), 5.25),
            letterSpacing: tggSpacing(rem(-0.03), -0.075),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.7), 4.25),
                lineHeight: tggSpacing(rem(2.1), 5.25),
            },
        },
        h5: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(2.1), 5.25),
            letterSpacing: tggSpacing(rem(-0.03), -0.075),
            color: theme.palette.common.blue,
        },
        h6: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            letterSpacing: tggSpacing(rem(-0.03), -0.075),
            color: theme.palette.common.blue,
        },
        caption: {
            fontSize: tggSpacing(rem(1.6), 4),
            fontFamily: 'thegymgroupsans-Bold',
            lineHeight: tggSpacing(rem(2.4), 6),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.6), 6.5),
            },
        },
        body1: {
            fontFamily: 'thegymgroupsans-Bold',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        body2: {
            fontFamily: 'thegymgroupsans-Bold',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            letterSpacing: 0,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        subtitle1: {
            fontFamily: 'thegymgroupsans-Bold',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.4), 3.5),
            color: theme.palette.common.blue,
            letterSpacing: 0,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
                lineHeight: tggSpacing(rem(1.8), 4.5),
            },
        },
        subtitle2: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.4), 3.5),
            color: theme.palette.common.blue,
            fontWeight: 'bold',
            letterSpacing: 0,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
                lineHeight: tggSpacing(rem(1.8), 4.5),
            },
        },
        bodyStandard: {
            fontSize: tggSpacing(rem(1.6), 4),
            fontFamily: 'thegymgroupsans-Bold',
            lineHeight: tggSpacing(rem(2.4), 6),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.6), 6.5),
            },
        },
        bodySmall: {
            fontSize: tggSpacing(rem(1.4), 3.5),
            fontFamily: 'thegymgroupsans-Bold',
            lineHeight: tggSpacing(rem(2), 5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        bodySmallBold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
                lineHeight: tggSpacing(rem(2), 5),
            },
        },
        bodyXSmall: {
            fontSize: tggSpacing(rem(1.2), 3),
            fontFamily: 'thegymgroupsans-Bold',
            lineHeight: tggSpacing(rem(1.8), 4.5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
                lineHeight: tggSpacing(rem(2), 5),
            },
        },
        bodyXXSmall: {
            fontSize: tggSpacing(rem(1), 2.5),
            fontFamily: 'thegymgroupsans-Bold',
            lineHeight: tggSpacing(rem(1.4), 3.5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
                lineHeight: tggSpacing(rem(1.8), 4.5),
            },
        },
        bodyXXSmallBold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.4), 3.5),
            letterSpacing: 0,
            color: theme.palette.common.blue,
            fontWeight: 'normal',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
                lineHeight: tggSpacing(rem(1.8), 4.5),
            },
        },
        text_10: {
            fontSize: tggSpacing(rem(1), 2.5),
        },
        text_10_lh_14: {
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.4), 3.5),
        },
        text_10_lh_20: {
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_10_lh_12: {
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.2), 3),
        },
        text_10_lh_16: {
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.6), 4),
        },
        text_10_desktop_16: {
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.6), 4),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        text_10_desktop_14: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.6), 4),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
            },
        },
        text_10_lh_10_desktop_14_lh_16: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1), 2.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
                lineHeight: tggSpacing(rem(1.6), 4),
            },
        },
        text_10_desktop_12: {
            fontSize: tggSpacing(rem(1), 2.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
            },
        },
        text_12_lh_14_desktop_14_lh_16: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(1.4), 3.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
                lineHeight: tggSpacing(rem(1.6), 4),
            },
        },
        text_12_lh_20_desktop_14_lh_22: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(2), 5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        text_12: {
            fontSize: tggSpacing(rem(1.2), 3),
        },
        text_12_lh_18: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(1.8), 4.5),
        },
        text_12_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.2), 3),
        },
        text_12_desktop_125: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.25), 3.125),
                lineHeight: tggSpacing(rem(2), 5),
            },
        },
        text_12_desktop_14: {
            fontSize: tggSpacing(rem(1.2), 3),
            fontFamily: 'thegymgroupsans-Bold',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.4), 3.5),
            },
        },
        text_12_lh_21: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(2.1), 5.25),
        },
        text_12_lh_22: {
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(2.2), 5.5),
        },
        text_12_desktop_15: {
            fontSize: tggSpacing(rem(1.2), 3),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.5), 3.75),
            },
        },
        text_14: {
            fontSize: tggSpacing(rem(1.4), 3.5),
            color: theme.palette.common.blue,
        },
        text_14_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
        },
        text_14_bold_lh_12_sans: {
            fontFamily: 'TGGSans',
            fontWeight: 700,
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(1.2), 3),
        },
        text_14_bold_desktop_16: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
            },
        },
        text_14_bold_desktop_18: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_14_lh_18_bold_desktop_18_lh_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_14_bold_lh_18: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            color: theme.palette.common.blue,
        },
        text_14_bold_lh_18_desktop_18_lh_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_14_bold_lh_20: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_14_lh_14: {
            fontSize: tggSpacing(rem(1.4), 3.5),
            color: theme.palette.common.blue,
        },
        text_14_lh_20: {
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_14_desktop_18: {
            fontSize: tggSpacing(rem(1.4), 3.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
            },
        },
        text_15: {
            fontSize: tggSpacing(rem(1.5), 3.75),
            color: theme.palette.common.blue,
        },
        text_15_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
        },
        text_15_bold_desktop_31: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.1), 7.75),
            },
        },
        text_15_bold_lh_21_desktop_17: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(2.1), 5.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.7), 4.25),
            },
        },
        text_15_bold_lh_24_desktop_17: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(2.4), 6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.7), 4.25),
            },
        },
        text_15_desktop_16: {
            fontSize: tggSpacing(rem(1.5), 3.75),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
            },
        },
        text_15_lh_16_desktop_18_lh_14: {
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: 1.6,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: 1.44,
            },
        },
        text_15_lh_16_desktop_18_lh_22: {
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(1.6), 4),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        text_15_lh_24_desktop_18_lh_26: {
            fontSize: tggSpacing(rem(1.5), 3.75),
            lineHeight: tggSpacing(rem(2.4), 6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.8), 4.5),
                lineHeight: tggSpacing(rem(2.6), 6.5),
            },
        },
        text_16: {
            fontSize: tggSpacing(rem(1.6), 4),
        },
        text_16_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.6), 4),
        },
        text_16_bold_desktop_15: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.6), 4),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.5), 3.75),
            },
        },
        text_16_bold_lh_24: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2.4), 6),
        },
        text_16_bold_lh_20_desktop_19: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2), 5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.9), 4.75),
            },
        },
        text_16_lh_15_desktop_lh_13: {
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(1.5), 3.75),
            [theme.breakpoints.up('desktop')]: {
                lineHeight: tggSpacing(rem(1.38), 3.45),
            },
        },
        text_16_lh_20: {
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_16_lh_20_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_16_lh_22: {
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2.2), 5.5),
        },
        text_16_lh_24: {
            fontSize: tggSpacing(rem(1.6), 4),
            lineHeight: tggSpacing(rem(2.4), 6),
        },
        text_17_letter_spacing_0_7: {
            fontSize: tggSpacing(rem(1.7), 4.25),
            letterSpacing: tggSpacing(rem(0.075), 0.1875),
        },
        text_18: {
            fontSize: tggSpacing(rem(1.8), 4.5),
        },
        text_18_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.8), 4.5),
        },
        text_18_bold_desktop_19: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.8), 4.5),
            [theme.breakpoints.up('desktop')]: {
                lineHeight: tggSpacing(rem(1.9), 4.75),
            },
        },
        text_18_bold_desktop_lh_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.8), 4.5),
            [theme.breakpoints.up('desktop')]: {
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_18_bold_lh_44: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.8), 4.5),
            lineHeight: tggSpacing(rem(4.4), 11),
        },
        text_18_lh_20: {
            fontSize: tggSpacing(rem(1.8), 4.5),
            lineHeight: tggSpacing(rem(2), 5),
        },
        text_18_lh_26: {
            fontSize: tggSpacing(rem(1.8), 4.5),
            lineHeight: tggSpacing(rem(2.6), 6.5),
        },
        text_19: {
            fontSize: tggSpacing(rem(1.9), 4.75),
        },
        text_19_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
        },
        text_19_bold_lh_175: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: 1.75,
        },
        text_19_bold_lh_22: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: tggSpacing(rem(2.2), 5.5),
        },
        text_19_bold_desktop_21: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.1), 5.25),
            },
        },
        text_19_bold_desktop_22: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.2), 5.5),
            },
        },
        text_19_bold_lh_22_desktop_21: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: tggSpacing(rem(2.2), 5.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.1), 5.25),
            },
        },
        text_19_bold_lh_21_desktop_21_lh_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: tggSpacing(rem(2.1), 5.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.1), 5.25),
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_19_bold_lh_22_desktop_21_lh_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: tggSpacing(rem(2.2), 5.5),
            letterSpacing: tggSpacing(rem(-0.03), -0.075),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.1), 5.25),
                lineHeight: tggSpacing(rem(2.5), 6.25),
            },
        },
        text_19_bold_lh_22_desktop_26_lh_30: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.9), 4.75),
            lineHeight: tggSpacing(rem(2.2), 5.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.6), 6.5),
                lineHeight: tggSpacing(rem(3), 7.5),
            },
        },
        text_20_bold_lh_22_desktop_25_lh_28_sans: {
            fontFamily: 'TGGSans',
            fontSize: '2rem',
            fontWeight: 800,
            lineHeight: '2.2rem',
            [theme.breakpoints.up('desktop')]: {
                fontSize: '2.5rem',
                lineHeight: '2.8rem',
            },
        },
        text_20: {
            fontSize: tggSpacing(rem(2), 5),
        },
        text_20_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2), 5),
        },
        text_21: {
            fontSize: tggSpacing(rem(2.1), 5.25),
        },
        text_21_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.1), 5.25),
        },
        text_21_bold_desktop_24: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.1), 5.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.4), 6),
            },
        },
        text_21_bold_desktop_27: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.1), 5.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.7), 6.75),
            },
        },
        text_23_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.3), 5.75),
        },
        text_23_bold_lh_28_desktop_25: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: '2.3rem',
            lineHeight: '2.8rem',
            letterSpacing: '-0.003rem',
            [theme.breakpoints.up('desktop')]: {
                fontSize: '2.5rem',
                lineHeight: '2.8rem',
                letterSpacing: '-0.004rem',
            },
        },
        text_25_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.5), 6.25),
        },
        text_25_bold_desktop_27: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.5), 6.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.7), 6.75),
            },
        },
        text_25_bold_lh_28_desktop_26_lh_30: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.5), 6.25),
            lineHeight: tggSpacing(rem(2.8), 7),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(2.6), 6.5),
                lineHeight: tggSpacing(rem(3), 7.5),
            },
        },
        text_25_bold_desktop_31: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.5), 6.25),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.1), 7.75),
            },
        },
        text_26_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.6), 6.5),
        },
        text_28_bold_lh_30: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.8), 7),
            lineHeight: tggSpacing(rem(3), 7.5),
        },
        text_28_bold_lh_30_desktop_36_lh_40: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: '2.8rem',
            lineHeight: '3rem',
            [theme.breakpoints.up('desktop')]: {
                fontSize: '3.6rem',
                lineHeight: '4rem',
                letterSpacing: '-0.005rem',
            },
        },
        text_14_bold_lh_20_desktop_16_lh_24: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: '1.4rem',
            lineHeight: '2rem',
            [theme.breakpoints.up('desktop')]: {
                fontSize: '1.6rem',
                lineHeight: '2.4rem',
            },
        },
        text_28_letter_spacing_0313: {
            fontSize: tggSpacing(rem(2.8), 7),
            letterSpacing: tggSpacing(rem(0.313), 0.7825),
        },
        text_28_bold_lh_31_desktop_36: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.8), 7),
            lineHeight: tggSpacing(rem(3.1), 7.75),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.6), 9),
            },
        },
        text_28_bold_desktop_36: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.8), 7),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.6), 9),
            },
        },
        text_28_bold_lh_30_desktop_36_lh_38: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(2.8), 7),
            lineHeight: tggSpacing(rem(3), 7.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(3.6), 9),
                lineHeight: tggSpacing(rem(3.8), 9.5),
            },
        },
        text_31_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(3.1), 7.75),
        },
        text_32_bold: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(3.2), 8),
        },
        text_36_lh_40_letter_spacing_negative_0_005: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(3.6), 9),
            lineHeight: tggSpacing(rem(4), 10),
            letterSpacing: tggSpacing(rem(-0.005), -0.0125),
        },
        text_40_bold_lh_20_desktop_54_lh_22: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(4), 10),
            lineHeight: tggSpacing(rem(2), 5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(5.4), 13.5),
                lineHeight: tggSpacing(rem(2.2), 5.5),
            },
        },
        heading_10_lh_12_white: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.2), 3),
            color: theme.palette.common.white,
            letterSpacing: 'normal',
        },
        heading_10_lh_16: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1), 2.5),
            lineHeight: tggSpacing(rem(1.6), 4),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.2), 3),
            },
        },
        heading_12_lh_18: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.2), 3),
            lineHeight: tggSpacing(rem(1.8), 4.5),
            color: theme.palette.common.blue,
        },
        heading_14_desktop_16: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: tggSpacing(rem(1.4), 3.5),
            lineHeight: tggSpacing(rem(2), 5),
            color: theme.palette.common.blue,
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(rem(1.6), 4),
            },
        },
        heading_14_desktop_16_lh_16: {
            fontFamily: 'thegymgroupsans-Headline',
            fontSize: '1.4rem',
            lineHeight: '1.6rem',
            fontWeight: 'normal',
            letterSpacing: '-0.05rem',
            [theme.breakpoints.up('desktop')]: {
                fontSize: '1.6rem',
                lineHeight: '1.8rem',
            },
        },
    };

    const rebrandTypography = {
        snackbarRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(5),
            lineHeight: tggSpacing(6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: `${tggSpacing(7)}`,
                lineHeight: `${tggSpacing(8)}`,
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: `${tggSpacing(7)}`,
                lineHeight: `${tggSpacing(8)}`,
            },
        },
        display1Rebrand: {
            fontFamily: 'ABCGravity',
            fontSize: tggSpacing(12),
            fontWeight: '900',
            lineHeight: tggSpacing(10),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(16),
                lineHeight: tggSpacing(14),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(24),
                lineHeight: tggSpacing(20),
            },
        },
        display2Rebrand: {
            fontFamily: 'ABCGravity',
            fontSize: tggSpacing(9),
            lineHeight: tggSpacing(8),
            fontWeight: '900',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(9),
                lineHeight: tggSpacing(8),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(9),
                lineHeight: tggSpacing(8),
            },
        },
        h1Rebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(7),
            lineHeight: tggSpacing(8),
            letterSpacing: '-0.14px',
            fontWeight: '700',
            [theme.breakpoints.up('desktop')]: {
                fontSize: `${tggSpacing(9)}`,
                lineHeight: `${tggSpacing(10)}`,
                letterSpacing: '-0.18px',
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: `${tggSpacing(9)}`,
                lineHeight: `${tggSpacing(10)}`,
                letterSpacing: '-0.18px',
            },
        },
        h2Rebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(6),
            lineHeight: tggSpacing(7),
            letterSpacing: '-0.096px',
            fontWeight: '700',
            [theme.breakpoints.up('desktop')]: {
                fontSize: `${tggSpacing(7)}`,
                lineHeight: `${tggSpacing(8)}`,
                letterSpacing: '-0.112px',
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: `${tggSpacing(7)}`,
                lineHeight: `${tggSpacing(8)}`,
                letterSpacing: '-0.112px',
            },
        },
        h3Rebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(5),
            lineHeight: tggSpacing(6),
            letterSpacing: ' -0.04px',
            fontWeight: '700',
            [theme.breakpoints.up('desktop')]: {
                fontSize: `${tggSpacing(6)}`,
                lineHeight: `${tggSpacing(7)}`,
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: `${tggSpacing(6)}`,
                lineHeight: `${tggSpacing(7)}`,
            },
        },
        h4Rebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(5),
            letterSpacing: '-0.016px',
            fontWeight: '700',
        },
        h5Rebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '700',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(4.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4.5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4.5),
            },
        },
        bodyLargeRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '400',
            fontSize: tggSpacing(4.5),
            lineHeight: tggSpacing(6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4.5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4.5),
            },
        },
        bodyLargeBoldRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '700',
            fontSize: tggSpacing(4.5),
            lineHeight: tggSpacing(6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4.5),
                lineHeight: tggSpacing(6),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4.5),
                lineHeight: tggSpacing(6),
            },
        },
        bodyStandardRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '400',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
        },
        bodyStandardBoldRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '700',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(6),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
        },
        bodySmallRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '400',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        bodySmallBoldRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '700',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        bodyXSmallRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '400',
            fontSize: tggSpacing(3),
            lineHeight: tggSpacing(4.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        bodyXSmallBoldRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '700',
            fontSize: tggSpacing(3),
            lineHeight: tggSpacing(4.5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3),
                lineHeight: tggSpacing(4.5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3),
                lineHeight: tggSpacing(4.5),
            },
        },
        buttonLabelSmallRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(4),
            fontWeight: '500',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(4),
            },
        },
        buttonLabelStandardRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(5),
            fontWeight: '500',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(5),
            },
        },
        buttonLabelLargeRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4.5),
            lineHeight: tggSpacing(5),
            fontWeight: '500',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4.5),
                lineHeight: tggSpacing(5),
            },
        },
        buttonTextLinkRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '400',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(5),
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(5),
            },
        },
        topNavigationRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(3),
            lineHeight: tggSpacing(4.5),
            fontWeight: '700',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3),
                lineHeight: tggSpacing(4.5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3),
                lineHeight: tggSpacing(4.5),
            },
        },
        fieldLabelRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(6),
            fontWeight: '400',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
        },
        fieldInputRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(6),
            fontWeight: '500',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(6),
            },
        },
        fieldErrorMessageRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(5),
            fontWeight: '400',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        helpTextRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(5),
            fontWeight: '400',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        tableLabelRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(5),
            fontWeight: '400',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
            [theme.breakpoints.up('largeDesktop')]: {
                fontSize: tggSpacing(3.5),
                lineHeight: tggSpacing(5),
            },
        },
        fieldInputBoldRebrand: {
            fontFamily: 'TGGSans',
            fontWeight: '500',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(6),
        },
        tabLabelSmallRebrand: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(3.5),
            lineHeight: tggSpacing(4),
            fontWeight: '500',
            [theme.breakpoints.up('desktop')]: {
                fontSize: tggSpacing(4),
                lineHeight: tggSpacing(4),
            },
        },
        linkWithChevron: {
            fontFamily: 'TGGSans',
            fontSize: tggSpacing(4),
            lineHeight: tggSpacing(5),
        },
    };

    themeWithOverrides.typography = {
        ...theme.typography,
        ...overrideTypography,
        ...rebrandTypography,
    };

    return {
        ...themeWithOverrides,
        pxToRem,
        tggSpacing,
        isRebranding: isRebrandingCreator(isRebrandingEnabled),
        getRebrandingColor: getRebrandingColorCreator(isRebrandingEnabled),
        tgg: 'tgg',
    };
};

export const promoPalette: ThemePalette = {
    'dark blue': {
        background: palette.common.blue,
        text: palette.common.white,
    },
    'cyan blue': {
        background: palette.primary.main,
        text: palette.common.blue,
    },
    'electric green': {
        background: palette.secondary.main,
        text: palette.common.blue,
    },
    white: {
        background: palette.common.white,
        text: palette.common.blue,
    },
    red: {
        background: palette.pino.ultimate,
        text: palette.common.blue,
    },
    orange: {
        background: palette.warning.main,
        text: palette.common.blue,
    },
    '5% tint of cyan': {
        background: palette.grey[50],
        text: palette.common.blue,
    },
    '10% tint of cyan': {
        background: palette.grey[100],
        text: palette.common.blue,
    },
    '17% tint of cyan': {
        background: palette.grey[200],
        text: palette.common.blue,
    },
    '22% tint of cyan': {
        background: palette.grey[900],
        text: palette.common.blue,
    },
    'silver grey': {
        background: palette.product.silverGrey,
        text: palette.common.blue,
    },
};

export type PromoColors = keyof typeof promoPalette;

export const defaultTggTheme = createTggTheme();
